/**
 * Profile context provider - React state management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { useToast } from '@/components/ui/use-toast';
import { useSubscription } from '@/contexts/subscription';
import type { Profile, ProfileContextType } from './types';
import {
  loadProfiles,
  determineActiveProfile,
  createProfile,
  updateProfileOperation,
  deleteProfileOperation,
  clearProfileDataOperation,
  setCurrentActiveProfileId
} from './operations';

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfileState] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  const { user } = useAuth();
  const { toast } = useToast();
  const { planLimits } = useSubscription();

  // Load profiles when user authenticates
  useEffect(() => {
    const initProfiles = async () => {
      if (!user) {
        console.log('[Profiles] No user authenticated, clearing profiles');
        setProfiles([]);
        setActiveProfileState(null);
        setIsProfileLoading(false);
        return;
      }

      setIsProfileLoading(true);
      
      try {
        const loadedProfiles = await loadProfiles();
        setProfiles(loadedProfiles);
        
        const active = determineActiveProfile(loadedProfiles);
        setActiveProfileState(active);
        
        console.log(`[Profiles] Loaded ${loadedProfiles.length} profiles, active: ${active?.name}`);
      } catch (error) {
        console.error('[Profiles] Error loading profiles:', error);
        toast({
          title: 'Erro ao carregar perfis',
          description: 'Não foi possível carregar seus perfis. Tente novamente.',
          variant: 'destructive'
        });
        setProfiles([]);
        setActiveProfileState(null);
      } finally {
        setIsProfileLoading(false);
      }
    };

    initProfiles();
  }, [user, toast]);

  const setActiveProfile = (profile: Profile | null) => {
    setActiveProfileState(profile);
    if (profile) {
      setCurrentActiveProfileId(profile.id);
      console.log(`[Profiles] Active profile set to: ${profile.name}`);
    }
  };

  const addProfile = async (profileData: Omit<Profile, 'id' | 'createdAt'>): Promise<Profile> => {
    // Enforce subscription limits
    const max = planLimits.maxProfiles;
    if (max !== 'unlimited' && profiles.length >= max) {
      toast({
        title: 'Limite de perfis atingido',
        description: `Seu plano atual permite até ${max} perfis. Atualize seu plano para criar mais perfis.`,
        variant: 'destructive'
      });
      throw new Error('Limite de perfis atingido');
    }

    try {
      const newProfile = await createProfile(profileData);
      setProfiles(prev => [...prev, newProfile]);
      
      toast({
        title: 'Perfil criado',
        description: `O perfil "${newProfile.name}" foi criado com sucesso.`
      });
      
      return newProfile;
    } catch (error) {
      console.error('[Profiles] Error creating profile:', error);
      toast({
        title: 'Erro ao criar perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const updateProfile = async (id: string, updates: Partial<Profile>): Promise<void> => {
    try {
      await updateProfileOperation(id, updates);
      
      setProfiles(prev => prev.map(profile => 
        profile.id === id ? { ...profile, ...updates } : profile
      ));
      
      if (activeProfile?.id === id) {
        setActiveProfileState(prev => prev ? { ...prev, ...updates } : null);
      }
      
      toast({
        title: 'Perfil atualizado',
        description: 'As alterações foram salvas com sucesso.'
      });
    } catch (error) {
      console.error('[Profiles] Error updating profile:', error);
      toast({
        title: 'Erro ao atualizar perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const deleteProfile = async (id: string): Promise<void> => {
    if (profiles.length <= 1) {
      throw new Error('Não é possível excluir o último perfil');
    }

    try {
      // Clear profile data first
      await clearProfileDataOperation(id);
      
      // Delete from Supabase
      await deleteProfileOperation(id);
      
      setProfiles(prev => prev.filter(profile => profile.id !== id));
      
      // If the active profile is being deleted, set another one as active
      if (activeProfile?.id === id) {
        const remainingProfiles = profiles.filter(p => p.id !== id);
        const newActiveProfile = remainingProfiles.length > 0 ? remainingProfiles[0] : null;
        setActiveProfile(newActiveProfile);
      }
      
      toast({
        title: 'Perfil excluído',
        description: 'O perfil e todos os seus dados foram removidos.'
      });
    } catch (error) {
      console.error('[Profiles] Error deleting profile:', error);
      toast({
        title: 'Erro ao excluir perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const clearProfileData = async (profileId: string): Promise<void> => {
    try {
      await clearProfileDataOperation(profileId);
    } catch (error) {
      console.error('[Profiles] Error clearing profile data:', error);
      throw error;
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        activeProfile,
        isProfileLoading,
        setActiveProfile,
        addProfile,
        updateProfile,
        deleteProfile,
        clearProfileData,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export { ProfileContext };
